import { useMemo, useRef, useState } from "react";
import { ScrollView, Text, type TextInput } from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildModelDraft,
  buildPayload,
  coerceLocale,
  modelDayName,
  modelHasFocus,
  modelRowAt,
  modelRowExercise,
  pickLocalizedText,
  PROGRAM_FOCUS,
  SESSION_MINUTES,
  type EffortScale,
  type Exercise,
  type ModelLevel,
  type NewProgram,
  type ProgramFocus,
  type ProgramModel,
  type SessionMinutes,
} from "@heraion/shared";

import { difficultyLabel } from "../Library/difficultyLabel";
import { Button } from "../primitives/Button";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { FieldLabel } from "../primitives/FieldLabel";
import { TextField } from "../primitives/TextField";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import type { UiTheme } from "../theme";
import { ModelFocusRow } from "./ModelFocusRow";
import { ModelWizardDay, type WizardRow } from "./ModelWizardDay";
import { makeSetupStyles } from "./setupStyles";

/** The program name cap: Program.name's schema max (120). The editor's own
 *  field sets none, so a longer name would only fail at save. */
const NAME_MAX = 120;

/** The default time per session: the model's fullest (every exercise). */
const DEFAULT_MINUTES: SessionMinutes = SESSION_MINUTES[SESSION_MINUTES.length - 1]!;

/** «9-10»: how many days a round lasts when `days` sessions run `perWeek`
 *  times a week. */
const roundDays = (days: number, perWeek: number): string => {
  const d = (days * 7) / perWeek;
  return Math.floor(d) === Math.ceil(d) ? String(d) : `${Math.floor(d)}-${Math.ceil(d)}`;
};

// A program model's wizard (#351, brief §15 row 33, flow 8).
// Choices: the name, the level, the focus (#356, only when a row changes
// with it; else the model's own), the minutes per session, the sessions a week and the length.
// Each offers only the model's own values.
// Then each day's rows: swap the exercise for an alternative, or take it out.
// Everything has a default, so after the name «Create» works in one tap.
// «Create» builds the draft (buildModelDraft) and hands its payload to the
// composer as the creation seed. It is never grey: tapped too early, it says
// what is missing, and takes the focus to the name when that is it.
export function ModelWizard({
  theme,
  model,
  catalog,
  effortScale,
  onCreate,
  onBack,
}: {
  theme: UiTheme;
  /** No id: a model just saved has none until the server's list is read.
   *  With one, the program keeps its origin (#356). */
  model: Omit<ProgramModel, "id"> & { id?: string };
  catalog: ReadonlyArray<Exercise> | undefined;
  effortScale: EffortScale;
  onCreate: (seed: NewProgram) => void;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const resolveName = useExerciseNameResolver(catalog);
  const rounds = model.roundPattern.length;
  // The time only matters when a row can drop out of a shorter session.
  const hasOptionalRows = model.days.some((d) => d.rows.some((r) => r.weight !== "main"));
  const hasFocus = modelHasFocus(model);
  const [name, setName] = useState(() => pickLocalizedText(model.name, lang));
  const [level, setLevel] = useState<ModelLevel>(model.levels[0]!);
  // The chips start on the focus the model is written in.
  const [focus, setFocus] = useState<ProgramFocus>(model.focus ?? PROGRAM_FOCUS[0]);
  // No choice to make: the program takes the focus the model is written in.
  const atFocus = hasFocus ? focus : model.focus;
  const [minutes, setMinutes] = useState<SessionMinutes>(DEFAULT_MINUTES);
  const [perWeek, setPerWeek] = useState(model.sessionsPerWeek[0]!);
  const [blocks, setBlocks] = useState(model.blocks[0]!);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const nameRef = useRef<TextInput>(null);
  const scrollRef = useRef<ScrollView>(null);
  const dayY = useRef<Record<string, number>>({});

  const days = useMemo(
    () =>
      model.days.map((day) => ({
        id: day.id,
        name: modelDayName(day, atFocus, lang),
        rows: day.rows.flatMap((row): WizardRow[] => {
          const entry = modelRowAt(row, level, minutes, atFocus);
          return entry
            ? [{ id: row.id, exercises: entry.exercises, exercise: modelRowExercise(entry, picks[row.id]) }]
            : [];
        }),
      })),
    [model, lang, level, minutes, atFocus, picks],
  );
  const toggleRemove = (id: string) => {
    // A row taken out has no alternatives to show.
    setOpen(null);
    setRemoved((old) => {
      const next = new Set(old);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };
  const empty = days.some((d) => d.rows.every((r) => removed.has(r.id)));
  const noName = name.trim().length === 0;
  const why = !tried
    ? null
    : noName
      ? t("editorV3.tetrad.needName", { defaultValue: "Give the program a name first." })
      : empty
        ? t("editorV3.tetrad.needDay", { defaultValue: "Each day needs at least one exercise: put one back." })
        : null;

  const create = () => {
    if (noName || empty) {
      setTried(true);
      if (noName) nameRef.current?.focus();
      else {
        // The empty day says so in place: take it into view.
        const bare = days.find((d) => d.rows.every((x) => removed.has(x.id)));
        const y = bare ? dayY.current[bare.id] : undefined;
        if (y !== undefined) scrollRef.current?.scrollTo({ y, animated: true });
      }
      return;
    }
    const kept: Omit<ProgramModel, "id"> = {
      ...model,
      days: model.days.map((d) => ({ ...d, rows: d.rows.filter((r) => !removed.has(r.id)) })),
    };
    const draft = buildModelDraft(kept, {
      name: name.trim(),
      level,
      ...(atFocus ? { focus: atFocus } : {}),
      minutes,
      sessionsPerWeek: perWeek,
      blocks,
      exercisePicks: picks,
      effortScale,
      editLocale: lang,
      exerciseName: (slug) => resolveName(slug),
      ...(model.id ? { modelId: model.id } : {}),
    });
    // A model has no kg (§15 row 29): the unit is never read.
    const built = buildPayload(draft, "kg");
    if (built.ok) onCreate(built.payload);
  };

  const minutesHint =
    minutes === 45
      ? t("editorV3.model.minutes45", { defaultValue: "No optional exercises, and one set fewer on the accessories." })
      : minutes === 60
        ? t("editorV3.model.minutes60", { defaultValue: "No optional exercises." })
        : t("editorV3.model.minutes75", { defaultValue: "Every exercise of the model." });

  // A choice with one option is not shown: its fixed value is said here.
  const fixed = [
    ...(model.sessionsPerWeek.length > 1
      ? []
      : [t("editorV3.tetrad.perWeekA11y", { count: perWeek, defaultValue: "{{count}} sessions a week" })]),
    ...(model.blocks.length > 1
      ? []
      : [t("editorV3.tetrad.roundsA11y", { count: blocks * rounds, defaultValue: "{{count}} rounds" })]),
    ...(model.levels.length > 1 ? [] : [difficultyLabel(level, t)]),
  ];
  const fixedHint = fixed.length > 0 ? fixed.join(" · ") : null;

  return (
    <ScrollView ref={scrollRef} contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Text style={styles.title} accessibilityRole="header">
        {pickLocalizedText(model.name, lang)}
      </Text>
      <Text style={styles.hint}>{pickLocalizedText(model.description, lang)}</Text>
      <FieldLabel theme={theme}>{t("program.name", { defaultValue: "Name" })}</FieldLabel>
      <TextField
        ref={nameRef}
        theme={theme}
        touchFloor
        value={name}
        onChangeText={setName}
        maxLength={NAME_MAX}
        selectTextOnFocus
        accessibilityLabel={t("program.name", { defaultValue: "Name" })}
      />
      {model.levels.length > 1 ? (
        <ChipChoiceRow
          theme={theme}
          choices={model.levels}
          selected={level}
          onSelect={setLevel}
          label={(l) => difficultyLabel(l, t)}
          chipA11yLabel={(l) => difficultyLabel(l, t)}
          groupA11yLabel={t("editorV3.model.level", { defaultValue: "Level" })}
          title={t("editorV3.model.level", { defaultValue: "Level" })}
        />
      ) : null}
      {fixedHint ? <Text style={styles.hint}>{fixedHint}</Text> : null}
      {hasFocus ? (
        <ModelFocusRow theme={theme} focus={focus} onSelect={setFocus} hintStyle={styles.hint} />
      ) : null}
      {hasOptionalRows ? (
        <>
        <ChipChoiceRow
          theme={theme}
          choices={SESSION_MINUTES}
          selected={minutes}
          onSelect={setMinutes}
          label={String}
          chipA11yLabel={(n) => t("editorV3.model.minutesA11y", { count: n, defaultValue: "{{count}} minutes" })}
          groupA11yLabel={t("editorV3.model.minutes", { defaultValue: "Minutes per session" })}
          title={t("editorV3.model.minutes", { defaultValue: "Minutes per session" })}
        />
        <Text style={styles.hint}>{minutesHint}</Text>
        </>
      ) : null}
      {model.sessionsPerWeek.length > 1 ? (
        <ChipChoiceRow
          theme={theme}
          choices={model.sessionsPerWeek}
          selected={perWeek}
          onSelect={setPerWeek}
          label={String}
          chipA11yLabel={(n) => t("editorV3.tetrad.perWeekA11y", { count: n, defaultValue: "{{count}} sessions a week" })}
          groupA11yLabel={t("editorV3.tetrad.perWeek", { defaultValue: "Sessions a week" })}
          title={t("editorV3.tetrad.perWeek", { defaultValue: "Sessions a week" })}
        />
      ) : null}
      {perWeek !== model.days.length ? (
        <Text style={styles.hint}>
          {t("editorV3.model.rotateHint", {
            count: model.days.length,
            length: roundDays(model.days.length, perWeek),
            defaultValue: "The {{count}} days take turns: a round lasts {{length}} days.",
          })}
        </Text>
      ) : null}
      {model.blocks.length > 1 ? (
        <ChipChoiceRow
          theme={theme}
          choices={model.blocks}
          selected={blocks}
          onSelect={setBlocks}
          label={(b) => String(b * rounds)}
          chipA11yLabel={(b) => t("editorV3.tetrad.roundsA11y", { count: b * rounds, defaultValue: "{{count}} rounds" })}
          groupA11yLabel={t("editorV3.tetrad.rounds", { defaultValue: "Rounds" })}
          title={t("editorV3.tetrad.rounds", { defaultValue: "Rounds" })}
        />
      ) : null}
      {blocks > 1 ? (
        <Text style={styles.hint}>
          {t("editorV3.model.blocksHint", {
            count: blocks,
            defaultValue: "The model runs {{count}} times: each time starts again a step higher.",
          })}
        </Text>
      ) : null}
      {days.map((day) => (
        <ModelWizardDay
          key={day.id}
          theme={theme}
          name={day.name}
          rows={day.rows}
          removed={removed}
          open={open}
          nameOf={(slug) => resolveName(slug)}
          onLayoutY={(y) => {
            dayY.current[day.id] = y;
          }}
          onToggleRemove={toggleRemove}
          onToggleOpen={(id) => setOpen((o) => (o === id ? null : id))}
          onPick={(id, slug) => {
            setPicks((p) => ({ ...p, [id]: slug }));
            setOpen(null);
          }}
        />
      ))}
      <Button theme={theme} label={t("program.create", { defaultValue: "Create" })} onPress={create} full />
      {why ? (
        <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
          {why}
        </Text>
      ) : null}
      <Button theme={theme} variant="ghost" label={t("editorV3.tetrad.back", { defaultValue: "Back" })} onPress={onBack} full />
    </ScrollView>
  );
}
